<template>
  <div class="training-session-list-view pa-4">
    <!-- Header Neutro -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-btn icon small class="mr-2" v-if="filters.TevID" @click="backToEvents">
          <v-icon color="grey darken-3">mdi-arrow-left</v-icon>
        </v-btn>
        <v-icon color="primary" large class="mr-3">mdi-clock-time-four-outline</v-icon>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Sesiones de Capacitación
          </h2>
          <span class="text-caption grey--text text--darken-2" v-if="selectedEventName">
            Evento: <b>{{ selectedEventName }}</b>
          </span>
          <span class="text-caption grey--text text--darken-2" v-else>
            Programación de jornadas, instructores asignados y control de asistencia por fecha.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- Filtros de Búsqueda -->
    <v-card class="mb-5 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="4">
            <s-select
              label="Filtrar por Evento"
              :items="eventItems"
              v-model="filters.TevID"
              clearable
              @change="onEventFilterChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <s-select
              label="Filtrar por Capacitador"
              :items="trainerItems"
              v-model="filters.TrnID"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <s-date label="Fecha Sesión" v-model="filters.DateInitial" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn block color="primary" class="rounded-lg font-weight-bold white--text text-none py-4" elevation="0" :loading="loading" @click="fetchSessions">
              <v-icon left small>mdi-magnify</v-icon>
              Consultar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Grilla de Datos Principal -->
    <v-card class="rounded-xl border-light bg-white pa-4 mb-5" outlined elevation="0">
      <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="grey darken-3" class="mr-2">mdi-calendar-month</v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
            Sesiones Programadas ({{ sessions.length }})
          </span>
        </div>
        <v-btn color="primary" class="rounded-lg text-none font-weight-bold px-4 py-4" small elevation="0" @click="openCreateModal">
          <v-icon left small>mdi-plus</v-icon>
          Nueva Sesión
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="sessions"
        :loading="loading"
        dense
        class="border-rounded elevation-0"
        :items-per-page="15"
      >
        <template v-slot:[`item.TseSessionNumber`]="{ item }">
          <v-avatar color="grey lighten-3" size="30">
            <span class="font-weight-black text-caption grey--text text--darken-4">#{{ item.TseSessionNumber }}</span>
          </v-avatar>
        </template>

        <template v-slot:[`item.TseTopicTitle`]="{ item }">
          <div class="font-weight-bold grey--text text--darken-4">
            {{ item.TseTopicTitle || 'Sesión General' }}
          </div>
          <div class="text-caption grey--text text--darken-2" v-if="!filters.TevID">
            Evento: {{ item.TevName }}
          </div>
        </template>

        <template v-slot:[`item.TseDate`]="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.TseDate) }}</span>
        </template>

        <template v-slot:[`item.schedule`]="{ item }">
          <span>{{ item.TseStartTime }} - {{ item.TseEndTime }} ({{ item.TseDurationMinutes }}m)</span>
        </template>

        <template v-slot:[`item.attendance`]="{ item }">
          <div class="d-flex align-center">
            <v-chip x-small color="success" class="mr-1 font-weight-bold" dark>
              {{ item.TotalAttended }} asistieron
            </v-chip>
            <v-chip x-small color="error" class="font-weight-bold" dark v-if="item.TotalAbsent > 0">
              {{ item.TotalAbsent }} faltas
            </v-chip>
          </div>
        </template>

        <template v-slot:[`item.TseStatusName`]="{ item }">
          <v-chip small :color="getStatusColor(item.TseStatus)" dark class="font-weight-bold">
            {{ item.TseStatusName }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <!-- Botón Ver Grabación -->
          <v-tooltip bottom v-if="item.TseRecordingLink">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="indigo" v-bind="attrs" v-on="on" :href="item.TseRecordingLink" target="_blank">
                <v-icon small>mdi-video-vintage</v-icon>
              </v-btn>
            </template>
            <span>Ver Grabación de la Sesión</span>
          </v-tooltip>

          <!-- Botón Material / Presentación (PPT o Enlace) -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                :color="(item.TseMaterialPath || item.TseMaterialLink) ? 'orange darken-2' : 'blue-grey lighten-1'"
                v-bind="attrs"
                v-on="on"
                @click="openMaterialModal(item)"
              >
                <v-icon small>{{ (item.TseMaterialPath || item.TseMaterialLink) ? 'mdi-presentation' : 'mdi-presentation-play' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ (item.TseMaterialPath || item.TseMaterialLink) ? 'Ver / Gestionar Presentación (PPT)' : 'Subir Presentación / PPT' }}</span>
          </v-tooltip>

          <!-- Botón Formato Oficial PDF -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openPdfModal(item)">
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>Exportar Formato Oficial (PDF)</span>
          </v-tooltip>

          <!-- Botón Evidencia Firmada (Auditoría) -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                :color="item.TseSignedAttendancePath ? 'green darken-2' : 'blue-grey'"
                v-bind="attrs"
                v-on="on"
                @click="openSignedModal(item)"
              >
                <v-icon small>{{ item.TseSignedAttendancePath ? 'mdi-file-check' : 'mdi-file-certificate-outline' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ item.TseSignedAttendancePath ? 'Ver Evidencia Firmada' : 'Subir Evidencia Firmada (Auditoría)' }}</span>
          </v-tooltip>

          <!-- Botón de Acción Principal: Tomar Asistencia -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="success" v-bind="attrs" v-on="on" @click="goToAttendance(item)">
                <v-icon small>mdi-clipboard-check-outline</v-icon>
              </v-btn>
            </template>
            <span>Control de Asistencia</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openEditModal(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Editar Sesión</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="deactivateSession(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>Desactivar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Formulario -->
    <TrainingSessionFormModal
      v-model="modal.show"
      :edit-item="modal.item"
      :default-tev-i-d="filters.TevID"
      @saved="fetchSessions"
    />

    <!-- Modal Material / Presentación PPT -->
    <TrainingSessionMaterialModal
      v-model="materialModal.show"
      :session="materialModal.session"
      @saved="fetchSessions"
    />

    <!-- Modal Formato Oficial PDF -->
    <TrainingAttendancePdfModal
      v-model="pdfModal.show"
      :session="pdfModal.session"
      :participants="pdfModal.participants"
    />

    <!-- Modal Evidencia Firmada -->
    <TrainingSignedAttendanceModal
      v-model="signedModal.show"
      :session="signedModal.session"
      @saved="fetchSessions"
    />
  </div>
</template>

<script>
import SDate from "@/components/Utils/SDate.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import TrainingSessionService from "@/services/Management/TrainingSessionService";
import TrainingEventService from "@/services/Management/TrainingEventService";
import TrainerService from "@/services/Management/TrainerService";
import TrainingAttendanceService from "@/services/Management/TrainingAttendanceService";
import TrainingSessionFormModal from "./TrainingSessionFormModal.vue";
import TrainingSessionMaterialModal from "./TrainingSessionMaterialModal.vue";
import TrainingAttendancePdfModal from "../Attendance/TrainingAttendancePdfModal.vue";
import TrainingSignedAttendanceModal from "./TrainingSignedAttendanceModal.vue";

export default {
  name: "TrainingSessionList",
  components: {
    SDate,
    SSelect,
    TrainingSessionFormModal,
    TrainingSessionMaterialModal,
    TrainingAttendancePdfModal,
    TrainingSignedAttendanceModal,
  },
  data() {
    return {
      loading: false,
      selectedEventName: "",
      eventItems: [],
      trainerItems: [],
      filters: {
        TevID: null,
        TrnID: null,
        DateInitial: null,
        TseStatus: null,
        SecStatus: 1,
      },
      sessions: [],
      headers: [
        { text: "N°", value: "TseSessionNumber", align: "center", width: "60px" },
        { text: "Módulo / Tema", value: "TseTopicTitle", align: "start" },
        { text: "Capacitador", value: "TrnFullName", width: "180px" },
        { text: "Fecha", value: "TseDate", width: "110px" },
        { text: "Horario", value: "schedule", width: "150px" },
        { text: "Asistencia", value: "attendance", width: "160px" },
        { text: "Estado", value: "TseStatusName", align: "center", width: "110px" },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: "180px" },
      ],
      modal: {
        show: false,
        item: null,
      },
      pdfModal: {
        show: false,
        session: null,
        participants: [],
      },
      signedModal: {
        show: false,
        session: null,
      },
      materialModal: {
        show: false,
        session: null,
      },
    };
  },
  created() {
    if (this.$route.query.tevID) {
      this.filters.TevID = Number(this.$route.query.tevID);
      this.selectedEventName = this.$route.query.eventName || "";
    }
    this.loadFilterCatalogs();
    this.fetchSessions();
  },
  methods: {
    loadFilterCatalogs() {
      const reqId = this.$fun.getUserID();
      TrainingEventService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.eventItems = (res.data || []).map((e) => ({ text: e.TevName, value: e.TevID }));
      });
      TrainerService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.trainerItems = (res.data || []).map((t) => ({ text: t.TrnFullName, value: t.TrnID }));
      });
    },
    fetchSessions() {
      this.loading = true;
      const reqId = this.$fun.getUserID();
      TrainingSessionService.list(this.filters, reqId)
        .then((res) => {
          this.sessions = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.$fun.alert("Error al cargar sesiones de capacitación.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEventFilterChange(val) {
      const ev = this.eventItems.find((e) => e.value === val);
      this.selectedEventName = ev ? ev.text : "";
      this.fetchSessions();
    },
    formatDate(dt) {
      if (!dt) return "-";
      return dt.split("T")[0];
    },
    getStatusColor(st) {
      if (st === 1) return "info";
      if (st === 2) return "warning";
      if (st === 3) return "success";
      if (st === 4) return "error";
      return "grey";
    },
    openCreateModal() {
      this.modal.item = null;
      this.modal.show = true;
    },
    openEditModal(item) {
      this.modal.item = Object.assign({}, item);
      this.modal.show = true;
    },
    openPdfModal(item) {
      const reqId = this.$fun.getUserID();
      this.pdfModal.session = Object.assign({}, item);
      this.loading = true;
      TrainingAttendanceService.list({ TseID: item.TseID, SecStatus: 1 }, reqId)
        .then((res) => {
          this.pdfModal.participants = Array.isArray(res.data) ? res.data : [];
          this.pdfModal.show = true;
        })
        .catch(() => {
          this.$fun.alert("Error al cargar la lista de participantes para el PDF.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openSignedModal(item) {
      this.signedModal.session = Object.assign({}, item);
      this.signedModal.show = true;
    },
    openMaterialModal(item) {
      this.materialModal.session = Object.assign({}, item);
      this.materialModal.show = true;
    },
    goToAttendance(item) {
      this.$router.push({
        path: "/management/training/attendance",
        query: {
          tseID: item.TseID,
          tevID: item.TevID,
          sessionTitle: item.TseTopicTitle || `Sesión #${item.TseSessionNumber}`,
        },
      });
    },
    backToEvents() {
      this.$router.push("/management/training/events");
    },
    deactivateSession(item) {
      this.$fun.alert("¿Está seguro de desactivar esta sesión?", "question").then((r) => {
        if (r && r.value) {
          const reqId = this.$fun.getUserID();
          const payload = Object.assign({}, item, { SecStatus: 0 });
          TrainingSessionService.save(payload, reqId)
            .then(() => {
              this.$fun.alert("Sesión desactivada correctamente.", "success");
              this.fetchSessions();
            })
            .catch(() => {
              this.$fun.alert("Error al desactivar la sesión.", "error");
            });
        }
      });
    },
  },
};
</script>
